<template>
  <b-modal v-model="active" size="lg" :id="modalId" scrollable>
    <div slot="modal-header" minimize class="app-modal-header">
      <span class="app-modal-header__icon"><AppIcon name="calculator" /></span>
      <h5 class="app-modal-header__title">Liquidación de IVA</h5>
      <AccountingBookBadge :declared="declared" class="ml-2" />
    </div>

    <p class="small text-muted mb-3">
      Salda el IVA del mes: débito menos crédito, menos percepciones de IVA y el saldo a favor anterior. El resultado queda en IVA a pagar o en saldo a favor para el mes siguiente.
      Hacelo antes de bloquear el mes. Las retenciones de IVA se usan desde Finanzas: un egreso de su caja de Retenciones, imputado contra IVA a pagar al aprobarlo.
      <span v-if="last">Última: <b>{{ formatDate(last.date) }}</b> · {{ last.reference }}.</span>
    </p>

    <div class="iva-controls">
      <div>
        <label class="filter-label">Mes</label>
        <b-form-input type="month" size="sm" v-model="month" :max="maxMonth" @change="preview()"></b-form-input>
      </div>
      <b-spinner small class="mb-2" v-if="loading" />
    </div>

    <div v-if="data" class="mt-3">
      <b-alert show variant="danger" class="mb-2" v-for="issue in data.issues" :key="'i' + issue">{{ issue }}</b-alert>
      <b-alert show variant="warning" class="mb-2" v-for="warning in data.warnings" :key="'w' + warning">{{ warning }}</b-alert>

      <table class="iva-calc">
        <tbody>
          <tr>
            <td>IVA débito fiscal</td>
            <td class="iva-calc__amount">{{ formatCurrency(data.total_debit) }}</td>
          </tr>
          <tr>
            <td>− IVA crédito fiscal</td>
            <td class="iva-calc__amount">{{ formatCurrency(data.total_credit) }}</td>
          </tr>
          <tr v-for="row in computables" :key="row.accounting_accounts_id" class="iva-calc__computable">
            <td>
              <b-form-checkbox v-model="row.selected" @change="preview(true)">
                − {{ row.label }} <small class="text-muted">({{ row.code }} · saldo {{ formatCurrency(row.balance) }})</small>
              </b-form-checkbox>
            </td>
            <td class="iva-calc__amount">
              <b-input-group size="sm" prepend="$" class="iva-calc__input">
                <b-form-input type="number" step="0.01" min="0" :max="row.balance" v-model="row.amount" :disabled="!row.selected" @change="preview(true)"></b-form-input>
              </b-input-group>
            </td>
          </tr>
          <tr v-if="data.previous_favor">
            <td>− Saldo a favor del mes anterior</td>
            <td class="iva-calc__amount">{{ formatCurrency(data.previous_favor) }}</td>
          </tr>
          <tr class="iva-calc__result" :class="data.result >= 0 ? 'iva-calc__result--pay' : 'iva-calc__result--favor'">
            <td>{{ data.result >= 0 ? 'IVA a pagar' : 'Saldo a favor para el mes siguiente' }}</td>
            <td class="iva-calc__amount">{{ formatCurrency(Math.abs(data.result)) }}</td>
          </tr>
        </tbody>
      </table>

      <b-button variant="link" size="sm" class="px-0 mt-2" v-if="data.lines.length" @click="showLines = !showLines">
        <AppIcon :name="showLines ? 'chevron-up' : 'chevron-down'" class="mr-1" />{{ showLines ? 'Ocultar' : 'Ver' }} asiento ({{ data.lines.length }} renglones)
      </b-button>
      <b-table v-if="showLines"
               class="app-table app-table--compact mb-0"
               :items="data.lines"
               :fields="lineFields"
               :small="true">
        <template v-slot:cell(account)="row">
          <span :class="{ 'pl-4': row.item.type == 'Haber' }">{{ row.item.accounting_accounts_code }} · {{ row.item.accounting_accounts_name }}</span>
        </template>
        <template v-slot:cell(debit)="row"><b v-if="row.item.type == 'Debe'">{{ formatCurrency(row.item.total) }}</b></template>
        <template v-slot:cell(credit)="row"><b v-if="row.item.type == 'Haber'">{{ formatCurrency(row.item.total) }}</b></template>
      </b-table>

      <b-row class="mt-3">
        <b-col md="5">
          <b-form-group label="Referencia" class="mb-0">
            <b-form-input type="text" size="sm" v-model="reference"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="7">
          <b-form-group label="Observaciones" class="mb-0">
            <b-form-input type="text" size="sm" v-model="observations"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <div slot="modal-footer">
      <b-button variant="outline-secondary" size="sm" class="mr-1" @click="active = false"><AppIcon name="close" class="mr-1" />Cancelar</b-button>
      <b-button variant="dark" size="sm" :disabled="loading || !data || !data.ready || !reference.trim()" @click="generate()"><AppIcon name="save" class="mr-1" />Generar liquidación</b-button>
    </div>
  </b-modal>
</template>
<script>
  import serviceAPI from '../services'
  import Error from '@/handler/error'
  import moment from 'moment'
  import AccountingBookBadge from './AccountingBookBadge'

  // Liquidación mensual de IVA de un libro.
  export default {
    components: {
      AccountingBookBadge,
    },
    props: {
      businessId: {
        default: null,
      },
      declared: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        active: false,
        modalId: 'iva_' + Math.random().toString(36).slice(2, 8),
        maxMonth: moment().subtract(1, 'month').format('YYYY-MM'),
        month: '',
        data: null,
        computables: [],
        last: null,
        loading: false,
        showLines: false,
        reference: '',
        observations: 'Liquidación de IVA',
        lineFields: [
          { key: 'account', label: 'Cuenta', class: 'align-middle' },
          { key: 'debit',   label: 'Debe',   class: 'align-middle text-right' },
          { key: 'credit',  label: 'Haber',  class: 'align-middle text-right' },
        ],
      }
    },
    methods: {
      // last: última liquidación del libro; el mes sugerido es el siguiente a ella o el anterior al actual
      open(last = null) {
        this.last = last
        const next = last ? moment(last.date).add(1, 'month').format('YYYY-MM') : this.maxMonth
        this.month = next < this.maxMonth ? next : this.maxMonth
        this.data = null
        this.computables = []
        this.showLines = false
        this.observations = 'Liquidación de IVA'
        this.active = true
        this.preview()
      },
      // withChoice: manda lo que tildó y corrigió el contador; si no, el servidor sugiere
      preview(withChoice = false) {
        if(!this.month) return
        this.loading = true
        serviceAPI.previewLiquidacionIva({
          business_id: this.businessId,
          declared:    this.declared,
          month:       this.month,
          computables: withChoice ? this.choice() : null,
        })
        .then(response => {
          this.data = response.data
          this.computables = response.data.computables.map(row => Object.assign({}, row))
          this.reference = 'LIQ-IVA-' + this.month
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      choice() {
        return this.computables.map(row => ({ accounting_accounts_id: row.accounting_accounts_id, selected: row.selected, amount: row.amount }))
      },
      generate() {
        this.loading = true
        serviceAPI.generarLiquidacionIva({
          business_id:  this.businessId,
          declared:     this.declared,
          month:        this.month,
          computables:  this.choice(),
          reference:    this.reference,
          observations: this.observations,
        })
        .then(response => {
          this.$awn.success('Liquidación de IVA generada')
          this.active = false
          this.$emit('saved', response.data)
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      formatDate(date) {
        return date ? moment(date).format('DD/MM/YYYY') : ''
      },
      formatCurrency(value) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value || 0)
      },
    },
  }
</script>
<style scoped>
  .iva-controls {
    display: flex;
    align-items: flex-end;
    gap: 8px;
  }
  .filter-label {
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 2px;
    color: var(--app-color-text-secondary);
    display: block;
  }
  .iva-calc {
    width: 100%;
    font-size: 14px;
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    border-collapse: separate;
    border-spacing: 0;
    overflow: hidden;
  }
  .iva-calc td {
    padding: 8px 12px;
    border-bottom: 1px solid var(--app-color-border);
    vertical-align: middle;
  }
  .iva-calc__amount {
    width: 200px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .iva-calc__input {
    width: 180px;
    margin-left: auto;
  }
  .iva-calc__result td {
    font-size: 16px;
    font-weight: 700;
    border-bottom: 0;
  }
  .iva-calc__result--pay {
    background: var(--app-color-warning-soft);
  }
  .iva-calc__result--favor {
    background: var(--app-color-success-soft);
  }
</style>
